import { Component } from 'react';
import { Icon, Spinner, Tab, Tabs } from '@blueprintjs/core';
import { SplitDiff, stringifyForDiff } from '@gonfalon/ui/Diff';
import { Dialog, IconButton, Modal, ModalOverlay } from '@launchpad-ui/components';
import { type Subscription } from 'rxjs';

import { type RequestError } from '../../../shared';
import { Box } from '../../../shared/components';
import { getAccountCustomRoles, getAccountRoleVersionAtTime } from '../../api';
import { buildAuditLogEntryDeepLink } from '../../auditLogLinks';
import {
  type Account,
  type AuditLogEntryDelta,
  type AuditLogEntryDetail,
  type CustomRole,
} from '../../model';

import { AuditLogEntryOverview } from './AuditLogEntryOverview';
import { CopyLinkButton } from './CopyLinkButton';

import './styles.scss';

type StateProps = Readonly<{
  account: Account;
  entryUrl: string;
  entryDetails?: AuditLogEntryDetail | null;
  entryDetailsError?: RequestError | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  enableAuditLogEntryLinks?: boolean;
  enableUpdatedRoleDiffs?: boolean;
  enableActorDisplay?: boolean;
}>;

type MemberVersionSnapshot = Readonly<{
  role?: string;
  customRoles?: readonly string[];
}>;

type State = {
  customRoles?: readonly CustomRole[];
  customRolesStatus?: 'loading' | 'ok' | 'error';
  creationPolicy?: CustomRole['policy'] | null;
  roleSnapshots?: { [roleId: string]: CustomRole | null };
};

export class AuditLogEntryDetails extends Component<StateProps, State> {
  public state: State = {};

  private customRolesSubscription?: Subscription;
  private roleVersionSubscription?: Subscription;
  private roleSnapshotSubscriptions: Subscription[] = [];
  private roleSnapshotsRequested = false;

  public componentDidMount() {
    this.loadRoleDiffs();
  }

  public componentDidUpdate(prevProps: StateProps) {
    if (prevProps.entryUrl !== this.props.entryUrl) {
      this.resetRoleDiffs();
      return;
    }
    this.loadRoleDiffs();
  }

  public componentWillUnmount() {
    this.clearRoleDiffRequests();
  }

  // One drawer instance is reused for every entry. Drop the previous entry's
  // role diffs before loading the next one. The follow-up render loads them.
  private resetRoleDiffs = () => {
    this.clearRoleDiffRequests();
    this.setState({
      customRoles: undefined,
      customRolesStatus: undefined,
      creationPolicy: undefined,
      roleSnapshots: undefined,
    });
  };

  private clearRoleDiffRequests = () => {
    this.customRolesSubscription?.unsubscribe();
    this.customRolesSubscription = undefined;
    this.roleVersionSubscription?.unsubscribe();
    this.roleVersionSubscription = undefined;
    this.roleSnapshotSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.roleSnapshotSubscriptions = [];
    this.roleSnapshotsRequested = false;
  };

  private loadRoleDiffs = () => {
    if (!this.props.enableUpdatedRoleDiffs) {
      return;
    }
    this.fetchCustomRolesIfNeeded();
    this.fetchCreationPolicyIfNeeded();
    this.fetchMemberRoleSnapshotsIfNeeded();
  };

  public render() {
    const {
      account,
      entryUrl,
      entryDetails,
      entryDetailsError,
      enableActorDisplay,
      enableAuditLogEntryLinks,
      isOpen,
      onOpenChange,
    } = this.props;
    const entryId = entryDetails?._id ?? this.entryIdFromUrl(entryUrl);

    return (
      <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
        <Modal size="large" variant="drawer">
          <Dialog aria-label="Audit log entry details">
            <div className="AuditLogEntryDetails-drawerHeader">
              {enableAuditLogEntryLinks && entryId && (
                <CopyLinkButton
                  url={buildAuditLogEntryDeepLink(account._id, entryId)}
                  ariaLabel="Copy link to this audit log entry"
                  className="AuditLogEntryDetails-modalCopyLink"
                />
              )}
              <IconButton
                icon="cancel"
                size="medium"
                variant="minimal"
                className="closeDrawer"
                aria-label="Close drawer"
                onPress={() => onOpenChange(false)}
              />
            </div>
            {this.renderBody(entryDetails, entryDetailsError, account, enableActorDisplay)}
          </Dialog>
        </Modal>
      </ModalOverlay>
    );
  }

  private entryIdFromUrl = (entryUrl: string): string | undefined => {
    const [pathOnly] = entryUrl.split(/[?#]/);
    const segments = pathOnly.split('/').filter(Boolean);
    return segments[segments.length - 1] || undefined;
  };

  private renderBody = (
    entryDetails: AuditLogEntryDetail | null | undefined,
    entryDetailsError: RequestError | null | undefined,
    account: Account,
    enableActorDisplay?: boolean,
  ) => {
    if (entryDetailsError) {
      return <div className="AuditLogEntryDetails-error">We couldn’t load this audit log entry.</div>;
    }
    if (!entryDetails || entryDetails.isFetching) {
      return <Spinner className="AuditLogEntryDetails-spinner" size={25} />;
    }
    return (
      <div className="AuditLogEntryDetails">
        <AuditLogEntryOverview entry={entryDetails} account={account} enableActorDisplay={enableActorDisplay} />
        {entryDetails.comment && <Box className="AuditLogEntry-comment"> {entryDetails.comment} </Box>}
        <Tabs id="entrytabs" className="AuditLogEntryDetails-Tabs" defaultSelectedTabId="diff">
          {entryDetails.delta && <Tab id="patch" title="Patch" panel={this.displayJSON(entryDetails.delta)} />}
          <Tab id="diff" title={this.diffTabTitle()} panel={this.displayDiff()} />
        </Tabs>
      </div>
    );
  };

  private displayJSON = (json: object | AuditLogEntryDelta) => (
    <div className="AuditLogEntryDetails-tabcontent">
      <pre>{JSON.stringify(json, null, 2)}</pre>
    </div>
  );

  private diffTabTitle = () => {
    const { entryDetails } = this.props;
    return entryDetails?.previousVersion && entryDetails?.currentVersion ? 'Diff' : 'Snapshot';
  };

  private displayDiff = () => {
    const { entryDetails } = this.props;
    const previousVersion = entryDetails?.previousVersion;
    const currentVersion = entryDetails?.currentVersion;

    if (previousVersion && currentVersion) {
      return (
        this.displayMemberRoleChanges(previousVersion, currentVersion) ??
        this.displaySplitDiff(previousVersion, currentVersion)
      );
    }
    if (currentVersion) {
      return (
        this.displayOneSidedMemberRoles(currentVersion, 'after') ?? (
          <>
            <p>There’s no previous version to compare against. This is the resource after this change:</p>
            {this.displayJSON(currentVersion)}
          </>
        )
      );
    }
    if (previousVersion) {
      return (
        this.displayOneSidedMemberRoles(previousVersion, 'before') ?? (
          <>
            <p>There’s no later version to compare against. This is the resource before this change:</p>
            {this.displayJSON(previousVersion)}
          </>
        )
      );
    }
    return this.displayCurrentRolePolicyFallback() ?? <p>We couldn’t generate a diff for this update.</p>;
  };

  private displaySplitDiff = (previousVersion: object, currentVersion: object) => (
    <>
      <div className="AuditLogEntryDetails-diffHeaders">
        <span>Previous version</span>
        <span>Updated version</span>
      </div>
      <SplitDiff
        original={stringifyForDiff(previousVersion)}
        compare={stringifyForDiff(currentVersion)}
        contextLines={3}
        showLineNumbers={false}
        isJSON
      />
    </>
  );

  private hasRoleChangeAction = () => {
    const accesses = this.props.entryDetails?.accesses ?? [];
    return accesses.some((a) => a.action === 'updateRole' || a.action === 'updateCustomRole');
  };

  private displayOneSidedMemberRoles = (version: object, side: 'before' | 'after') => {
    if (
      !this.props.enableUpdatedRoleDiffs ||
      this.props.entryDetails?.kind !== 'member' ||
      !this.hasRoleChangeAction()
    ) {
      return null;
    }
    const snapshot = version as MemberVersionSnapshot;
    return (
      <div className="AuditLogEntryDetails-memberRoles">
        <div className="AuditLogEntryDetails-diffHeaders AuditLogEntryDetails-memberRolesHeaders">
          <span>Roles {side} this change</span>
        </div>
        <div className="AuditLogEntryDetails-memberRolesColumns">
          {this.displayMemberRoleColumn(snapshot, snapshot.customRoles ?? [], false, 'added')}
        </div>
        {this.displayRolesNote(
          side === 'after'
            ? 'No earlier snapshot of this member exists, so previous roles aren’t shown — see the summary above. Expanded policies are as of this entry.'
            : 'No later snapshot of this member exists. Expanded policies are as of this entry.',
        )}
      </div>
    );
  };

  private displayRolesNote = (message: string) => (
    <div className="AuditLogEntryDetails-infoBox">
      <Icon icon="info-sign" iconSize={16} />
      <p>{message}</p>
    </div>
  );

  private displayMemberRoleChanges = (previousVersion: object, currentVersion: object) => {
    if (!this.props.enableUpdatedRoleDiffs || this.props.entryDetails?.kind !== 'member') {
      return null;
    }
    const previous = previousVersion as MemberVersionSnapshot;
    const current = currentVersion as MemberVersionSnapshot;
    const previousRoles = previous.customRoles ?? [];
    const currentRoles = current.customRoles ?? [];
    const baseRoleChanged = previous.role !== current.role;
    const customRolesChanged =
      previousRoles.length !== currentRoles.length || previousRoles.some((id) => !currentRoles.includes(id));
    if (!baseRoleChanged && !customRolesChanged) {
      return null;
    }
    return (
      <div className="AuditLogEntryDetails-memberRoles">
        <div className="AuditLogEntryDetails-diffHeaders AuditLogEntryDetails-memberRolesHeaders">
          <span>Roles before</span>
          <span>Roles after</span>
        </div>
        <div className="AuditLogEntryDetails-memberRolesColumns">
          {this.displayMemberRoleColumn(previous, currentRoles, baseRoleChanged, 'removed')}
          {this.displayMemberRoleColumn(current, previousRoles, baseRoleChanged, 'added')}
        </div>
        {this.displayRolesNote('Expanded policies show each role as of this entry, unless labeled otherwise.')}
      </div>
    );
  };

  private displayMemberRoleColumn = (
    snapshot: MemberVersionSnapshot,
    otherSideRoles: readonly string[],
    baseRoleChanged: boolean,
    changeKind: 'added' | 'removed',
  ) => {
    const roleIds = snapshot.customRoles ?? [];
    return (
      <ul>
        {snapshot.role && (
          <li
            className={`AuditLogEntryDetails-builtInRole${
              baseRoleChanged ? ` AuditLogEntryDetails-role--${changeKind}` : ''
            }`}
          >
            Built-in role: <strong>{snapshot.role}</strong>
          </li>
        )}
        {roleIds.map((roleId) => {
          const changed = !otherSideRoles.includes(roleId);
          const className = changed ? `AuditLogEntryDetails-role--${changeKind}` : undefined;
          return (
            <li key={roleId} className={className}>
              {this.displayMemberRole(roleId)}
            </li>
          );
        })}
        {roleIds.length === 0 && (
          <li>
            <em>No custom roles</em>
          </li>
        )}
      </ul>
    );
  };

  private displayMemberRole = (roleId: string) => {
    const liveRole = this.state.customRoles?.find((r) => r._id === roleId);
    const snapshot = this.state.roleSnapshots?.[roleId];
    const rolesResolved = this.state.customRolesStatus === 'ok';
    const isDeleted = rolesResolved && !liveRole;
    const name = snapshot?.name ?? liveRole?.name;

    if (!name) {
      if (snapshot === undefined) {
        return (
          <>
            {roleId} <em>(loading…)</em>
          </>
        );
      }
      return (
        <>
          {roleId} <em>{rolesResolved ? '(role no longer exists)' : '(unavailable)'}</em>
        </>
      );
    }

    return (
      <details>
        <summary>
          {name}
          {isDeleted && <span className="AuditLogEntryDetails-deletedTag">Deleted role</span>}
        </summary>
        {snapshot === undefined && (
          <p>
            <em>Loading policy…</em>
          </p>
        )}
        {snapshot === null && (
          <p>
            <em>Point-in-time snapshot unavailable — showing the role’s latest policy.</em>
          </p>
        )}
        {snapshot && isDeleted && (
          <p>
            <em>This role has since been deleted.</em>
          </p>
        )}
        {snapshot !== undefined && <pre>{JSON.stringify(snapshot?.policy ?? liveRole?.policy, null, 2)}</pre>}
      </details>
    );
  };

  private fetchMemberRoleSnapshotsIfNeeded = () => {
    const { account, entryDetails, entryUrl } = this.props;
    if (this.roleSnapshotsRequested) {
      return;
    }
    if (!entryDetails || entryDetails.isFetching || !entryUrl.endsWith(`/${entryDetails._id}`)) {
      return;
    }
    if (entryDetails.kind !== 'member' || (!entryDetails.previousVersion && !entryDetails.currentVersion)) {
      return;
    }
    const previous = entryDetails.previousVersion as MemberVersionSnapshot | undefined;
    const current = entryDetails.currentVersion as MemberVersionSnapshot | undefined;
    const previousRoles = previous?.customRoles ?? [];
    const currentRoles = current?.customRoles ?? [];
    if (previous && current) {
      const baseRoleChanged = previous.role !== current.role;
      const customRolesChanged =
        previousRoles.length !== currentRoles.length || previousRoles.some((id) => !currentRoles.includes(id));
      if (!baseRoleChanged && !customRolesChanged) {
        return;
      }
    } else if (!this.hasRoleChangeAction()) {
      return;
    }
    const roleIds = [...new Set([...previousRoles, ...currentRoles])];
    if (roleIds.length === 0) {
      return;
    }
    this.roleSnapshotsRequested = true;
    for (const roleId of roleIds) {
      this.roleSnapshotSubscriptions.push(
        getAccountRoleVersionAtTime(account, roleId, entryDetails.date).subscribe({
          next: (snapshot) => {
            this.setState((prevState) => ({ roleSnapshots: { ...prevState.roleSnapshots, [roleId]: snapshot } }));
          },
          error: () => {
            this.setState((prevState) => ({ roleSnapshots: { ...prevState.roleSnapshots, [roleId]: null } }));
          },
        }),
      );
    }
  };

  private displayCurrentRolePolicyFallback = () => {
    const { entryDetails } = this.props;
    if (!this.props.enableUpdatedRoleDiffs || entryDetails?.kind !== 'role') {
      return null;
    }
    if (this.state.customRolesStatus === 'loading' || this.state.customRolesStatus === undefined) {
      return <Spinner size={25} />;
    }
    const role = this.state.customRoles?.find((r) => r.name === entryDetails.name);
    if (!role) {
      return null;
    }
    if (this.state.creationPolicy === undefined) {
      return <Spinner size={25} />;
    }
    if (this.state.creationPolicy) {
      return (
        <>
          <p>
            Policy for <strong>{role.name}</strong> at the time of this entry
          </p>
          {this.displayJSON(this.state.creationPolicy)}
        </>
      );
    }
    return (
      <>
        <p>
          Latest policy for <strong>{role.name}</strong> <em>(may differ from the policy at the time of this entry)</em>
        </p>
        {this.displayJSON(role.policy)}
      </>
    );
  };

  private fetchCreationPolicyIfNeeded = () => {
    const { account, entryDetails, entryUrl } = this.props;
    if (this.state.creationPolicy !== undefined || this.roleVersionSubscription) {
      return;
    }
    if (!entryDetails || entryDetails.isFetching || !entryUrl.endsWith(`/${entryDetails._id}`)) {
      return;
    }
    if (entryDetails.kind !== 'role' || entryDetails.previousVersion || entryDetails.currentVersion) {
      return;
    }
    const role = this.state.customRoles?.find((r) => r.name === entryDetails.name);
    if (!role) {
      return;
    }
    this.roleVersionSubscription = getAccountRoleVersionAtTime(account, role._id, entryDetails.date).subscribe({
      next: (snapshot) => {
        this.setState({ creationPolicy: snapshot.policy ?? null });
      },
      error: () => {
        this.setState({ creationPolicy: null });
      },
    });
  };

  private fetchCustomRolesIfNeeded = () => {
    const { account, entryDetails, entryUrl } = this.props;
    if (this.state.customRolesStatus || this.customRolesSubscription) {
      return;
    }
    if (!entryDetails || entryDetails.isFetching || !entryUrl.endsWith(`/${entryDetails._id}`)) {
      return;
    }
    if (entryDetails.kind !== 'member' && entryDetails.kind !== 'role') {
      return;
    }
    this.setState({ customRolesStatus: 'loading' });
    this.customRolesSubscription = getAccountCustomRoles(account).subscribe({
      next: (response) => {
        this.setState({ customRoles: Object.values(response.entities.customRoles ?? {}), customRolesStatus: 'ok' });
      },
      error: () => {
        this.setState({ customRolesStatus: 'error' });
      },
    });
  };
}
